import { create } from 'zustand'
import { zeroize } from '@/services/CryptoService'

type MasterPasswordState = {
    /** Whether the user has a master password configured server-side. */
    isConfigured: boolean
    /** Whether the user has a TTL keypair configured server-side. */
    hasTtl: boolean
    /** Derived master key held in memory only. Null on page load / after logout. */
    masterKey: Uint8Array | null
    /** Whether the setup modal is open. */
    modalOpen: boolean
    /**
     * Action to retry after master password is successfully set up.
     * Stored so the original user action (e.g. insert secret field) can be
     * auto-retried without requiring the user to click again.
     */
    pendingAction: (() => void) | null
    /** Whether the unlock modal is open (existing key, need to re-derive). */
    unlockModalOpen: boolean
    /**
     * Action to retry after the master key is successfully unlocked.
     * Stored so the original user action can be auto-retried without
     * requiring the user to click again.
     */
    unlockPendingAction: (() => void) | null
}

type MasterPasswordActions = {
    setConfigured: (configured: boolean) => void
    setHasTtl: (has: boolean) => void
    setMasterKey: (key: Uint8Array) => void
    openModal: (pendingAction?: (() => void) | null) => void
    closeModal: () => void
    openUnlockModal: (pendingAction?: (() => void) | null) => void
    closeUnlockModal: () => void
    /**
     * Zeroize and clear the master key from memory.
     * Call this on logout / session end.
     */
    clearMasterKey: () => void
}

const initialState: MasterPasswordState = {
    isConfigured: false,
    hasTtl: false,
    masterKey: null,
    modalOpen: false,
    pendingAction: null,
    unlockModalOpen: false,
    unlockPendingAction: null,
}

export const useMasterPasswordStore = create<
    MasterPasswordState & MasterPasswordActions
>()((set, get) => ({
    ...initialState,

    setConfigured: (configured) => set({ isConfigured: configured }),

    setHasTtl: (has) => set({ hasTtl: has }),

    setMasterKey: (key) => set({ masterKey: key }),

    openModal: (pendingAction = null) =>
        set({ modalOpen: true, pendingAction }),

    closeModal: () => set({ modalOpen: false, pendingAction: null }),

    openUnlockModal: (pendingAction = null) =>
        set({ unlockModalOpen: true, unlockPendingAction: pendingAction }),

    closeUnlockModal: () =>
        set({ unlockModalOpen: false, unlockPendingAction: null }),

    clearMasterKey: () => {
        const { masterKey } = get()
        if (masterKey) {
            try {
                zeroize(masterKey)
            } catch {
                // sodium may already be unloaded on teardown — safe to swallow
            }
        }
        set({ masterKey: null })
    },
}))
